/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import {
  appButtonType,
  appCardStyle,
  appIconsPath,
  appInfoCardSize,
  validCharRegex,
} from '@utils/globalConstant';
import { useActivation } from '@context/MobileOne';
import { useA11y } from '@context/Utils';
import {
  ApiError,
  CardSupport,
  NavigationPageTitle,
  ButtonPrimary,
  FormInputField,
  CardInfobox,
  ButtonCodeRenew,
  ScrollToTop,
  ButtonCancelActivation,
  FocusFirstError,
  NavigationHeader,
} from '@core/index';
import { Form, Formik } from 'formik';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { tA11y } from '@utils/a11y/a11yHelpers';

export function EmailVerificationCode() {
  // Constants
  const timerSecondsContent = tA11y('ek_reg_2fa_resend-email_timer_sec'); // "120" (current value)
  const timerSeconds = timerSecondsContent.content;

  // States
  const [timeLeft, setTimeLeft] = useState(timerSeconds);
  const [resetTimer, setResetTimer] = useState(false);

  // Context
  const {
    isLoading,
    phoneNumberActivationForm,
    validationSchemaCode,
    verifyEmailCode,
    onResendCode,
    formError,
    setFormError,
    setStep0SubStep,
    verifyEmail
  } = useActivation();
  const { isGenericApiError } = useA11y();

  // Countdown timer
  useEffect(() => {
    if (timeLeft === 0) return;

    const timer = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Reset timer
  useEffect(() => {
    if (resetTimer) {
      setTimeLeft(timerSeconds);
      setResetTimer(false);
    }
  }, [resetTimer, onResendCode, timerSeconds]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds}`;
  };

  const handleAPIErrorLink = () => {
    const firstInput = document.querySelector('input');
    if (firstInput) {
      firstInput.focus();
    }
  }

  const ariaLabelTimer = `Die verbleibende Zeit: ${formatTime(timeLeft)} Minuten`; // BFSG: check if this is correct

  return (
    <>
      <ScrollToTop>
        <div className="min-dvh-100">
          <NavigationHeader />
          <AppPageContentWrapper>
            <div className="pt-32">
              <NavigationPageTitle
                title="ek_reg_2fa_hdl1"
                desc="ek_reg_2fa_txt1"
                replaceDesc="{EMAIL_ADDRESS}"
                isBackButton
                replaceDescBy={phoneNumberActivationForm.email}
                variant="lg"
                onButtonClick={() => setStep0SubStep(0)}
              />
            </div>
            {/* BFSG: ONLY for TESTING of API ERROR BOXES, REMOVE AFTER TESTING */}
            {/* <p className="pb-8">{`API response contains field: 'isDefaultMsg' ----> ${isGenericApiError}`}</p> */}
            <Formik
              initialValues={{ emailCode: '' }}
              validationSchema={validationSchemaCode}
              onSubmit={verifyEmailCode}>
              {({
                values: { emailCode },
                handleBlur,
                handleChange,
                handleSubmit,
                touched,
                errors,
                setFieldValue
              }) => (
                <Form className="mt-40" onSubmit={handleSubmit} noValidate>
                  <FormInputField
                    label="ek_global_2fa_code_lbl"
                    placeholder="ek_global_2fa_code_plc"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    id="emailCode"
                    name="emailCode"
                    value={emailCode}
                    onChange={(e) => {
                      setFormError(null);
                      handleChange({
                        target: {
                          id: 'emailCode',
                          name: 'emailCode',
                          value: e.target.value
                        }
                      });
                    }}
                    onClear={setFieldValue}
                    onBlur={handleBlur}
                    valid={touched.emailCode && !errors.emailCode}
                    invalid={
                      (touched.emailCode && errors.emailCode) || !!(formError && !isGenericApiError)
                    }
                    invalidMessage={touched.emailCode && errors.emailCode ? errors.emailCode : null}
                    formError={formError}
                    // limits to four digits
                    maxLength={4}
                    aria-describedby="emailCode-error"
                    checkCharRegex={validCharRegex.EMAIL_VERIFICATION_CODE}
                  />
                  {formError && (
                    <div className="form-text mt-40 ps-4" aria-live="polite">
                      <ApiError
                        message={formError}
                        isGenericApiError={isGenericApiError}
                        handleAPIErrorLink={handleAPIErrorLink}
                      />
                    </div>
                  )}
                  <div className="row justify-content-center">
                    <div className="col-lg-5 col-10 px-0 mx-auto">
                      <ButtonPrimary
                        buttonClass="my-40"
                        buttonType={appButtonType.PRIMARY}
                        buttonContent={tA11y('ek_reg_2fa_btn1')}
                        type="submit"
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                  <FocusFirstError />
                </Form>
              )}
            </Formik>
            <CardInfobox
              cardStyle={appCardStyle.SECONDARY}
              cardSize={appInfoCardSize.DEFAULT}
              cardIcon={appIconsPath.INFO}
              text="ek_reg_2fa_txt2"
            />
            <div className="my-40 d-flex justify-content-center">
              <ButtonCodeRenew
                label="ek_global_timer_lnk1"
                ariaLabelButton={tA11y('ek_global_timer_lnk1').ariaLabel}
                // timerText="ek_global_timer_txt1"
                iconLeft={appIconsPath.REFRESH}
                // onClick={() => onResendCode()}
                onClick={() => {
                  verifyEmail({ email: phoneNumberActivationForm.email });
                  setResetTimer(true);
                }}
                timer={formatTime(timeLeft)}
                ariaLabelTimer={ariaLabelTimer}
                disabled={timeLeft !== 0}
              />
            </div>
            <CardSupport />
            <div className="d-flex justify-content-center mt-32">
              <ButtonCancelActivation />
            </div>
          </AppPageContentWrapper>
        </div>
      </ScrollToTop>
    </>
  );
}

export default EmailVerificationCode;
